import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  Loader2, Shield, Sparkles, Send, RefreshCw,
  TrendingUp, TrendingDown, AlertTriangle, Eye, Scissors, Target,
} from 'lucide-react';
import MarkdownMessage from '../../../components/ui/markdown-message';
import { usePortfolioStore } from '../../../state/portfolioStore';
import SectionNav from '../../../components/layout/SectionNav';
import PageHeader from '../../../components/layout/PageHeader';
import { portfolioTabs } from '../../../nav/publicNav';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'];

const QUICK_PROMPTS = [
  'Should I rebalance my portfolio?',
  'Which stocks should I trim?',
  'Best sector bets right now?',
  'What are the biggest risks?',
  'Tax loss harvesting opportunities?',
  'Compare my top 5 holdings',
];

function ScoreGauge({ score }) {
  const color = score >= 80 ? 'text-emerald-500' : score >= 60 ? 'text-amber-500' : 'text-red-500';
  const bg = score >= 80 ? 'from-emerald-500/10 to-emerald-600/5' : score >= 60 ? 'from-amber-500/10 to-amber-600/5' : 'from-red-500/10 to-red-600/5';
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Needs Work' : 'High Risk';
  return (
    <div className={`text-center p-6 rounded-2xl bg-gradient-to-br ${bg}`}>
      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Portfolio Score</p>
      <p className={`text-5xl font-bold ${color}`}>{score}</p>
      <p className={`text-sm font-medium mt-1 ${color}`}>{label}</p>
    </div>
  );
}

function ActionCard({ action }) {
  const icons = { trim: Scissors, add: TrendingUp, hold: Eye };
  const colors = { trim: 'text-red-500', add: 'text-emerald-500', hold: 'text-blue-500' };
  const Icon = icons[action.action] || Target;
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30 border border-border/30">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${colors[action.action] || 'text-muted-foreground'}`} />
      <div>
        <p className="text-sm font-semibold">{action.symbol}</p>
        <p className="text-xs text-muted-foreground">{action.reason}</p>
      </div>
    </div>
  );
}

function CorrelationMatrix({ data }) {
  if (!data) return null;
  const { symbols, matrix } = data;
  if (symbols.length > 14) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px]">
        <thead><tr><th className="p-1" />{symbols.map((s) => <th key={s} className="p-1 font-medium text-muted-foreground whitespace-nowrap">{s}</th>)}</tr></thead>
        <tbody>
          {symbols.map((s, i) => (
            <tr key={s}>
              <td className="p-1 font-medium text-muted-foreground whitespace-nowrap">{s}</td>
              {matrix[i].map((val, j) => {
                const abs = Math.abs(val);
                const bg = i === j ? 'bg-emerald-100 dark:bg-emerald-900/30' : abs > 0.7 ? 'bg-red-100 dark:bg-red-900/20' : abs > 0.4 ? 'bg-amber-100 dark:bg-amber-900/20' : 'bg-secondary/30';
                return <td key={j} className={`p-1 text-center ${bg} rounded`}>{val.toFixed(2)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RiskGauge({ label, value, unit = '', description, good }) {
  const displayVal = value != null ? `${value}${unit}` : 'N/A';
  return (
    <div className="text-center p-4">
      <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${value == null ? 'text-muted-foreground' : good ? 'text-emerald-600' : 'text-foreground'}`}>{displayVal}</p>
      {description && <p className="text-[10px] text-muted-foreground mt-0.5">{description}</p>}
    </div>
  );
}

export default function AnalyticsPage() {
  const overview = usePortfolioStore((s) => s.overview);
  const overviewLoading = usePortfolioStore((s) => s.overviewLoading);
  const fetchOverview = usePortfolioStore((s) => s.fetchOverview);
  const risk = usePortfolioStore((s) => s.risk);
  const riskLoading = usePortfolioStore((s) => s.riskLoading);
  const fetchRisk = usePortfolioStore((s) => s.fetchRisk);
  const holdings = usePortfolioStore((s) => s.holdings);
  const fetchHoldings = usePortfolioStore((s) => s.fetchHoldings);
  const aiReview = usePortfolioStore((s) => s.aiReview);
  const aiReviewLoading = usePortfolioStore((s) => s.aiReviewLoading);
  const aiReviewError = usePortfolioStore((s) => s.aiReviewError);
  const fetchAiReview = usePortfolioStore((s) => s.fetchAiReview);
  const sendPortfolioChat = usePortfolioStore((s) => s.sendPortfolioChat);

  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    if (!holdings.length) fetchHoldings();
    if (!overview) fetchOverview();
    if (!risk) fetchRisk();
  }, [fetchHoldings, fetchOverview, fetchRisk, holdings.length, overview, risk]);

  const handleAskAI = async (q) => {
    const text = q || chatInput;
    if (!text.trim()) return;
    setChatHistory((prev) => [...prev, { role: 'user', content: text }]);
    setChatInput('');
    setChatLoading(true);
    const result = await sendPortfolioChat(text);
    setChatHistory((prev) => [...prev, { role: 'assistant', content: result.text }]);
    setChatLoading(false);
  };

  const sectorAllocation = overview?.sectorAllocation || [];
  const capAllocation = overview?.capAllocation || [];
  const allHoldings = overview?.holdings || [];
  const riskFlags = overview?.riskFlags || [];
  const holdingWeights = allHoldings.sort((a, b) => (b.weight || 0) - (a.weight || 0)).slice(0, 15).map((h) => ({ name: h.symbol, weight: h.weight || 0 }));

  // Only show full-page loading on first visit when we have nothing at all
  const initialLoading = !overview && !risk && !aiReview && overviewLoading;

  if (initialLoading && !holdings.length) {
    return (
      <div className="space-y-4">
        <SectionNav tabs={portfolioTabs} />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SectionNav tabs={portfolioTabs} />
      <PageHeader title="Analytics & AI" description="Portfolio intelligence powered by AI" />

      {/* ── AI Portfolio Review ─────────────────────────────────── */}
      <Card className="border-border/40 overflow-hidden">
        <CardHeader className="pb-2 flex-row items-center justify-between bg-gradient-to-r from-emerald-500/5 to-transparent">
          <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-emerald-500" /> AI Portfolio Review
          </CardTitle>
          <Button size="sm" variant="outline" className="text-xs h-7 gap-1.5" onClick={fetchAiReview} disabled={aiReviewLoading}>
            {aiReviewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {aiReview ? 'Regenerate' : 'Generate'}
          </Button>
        </CardHeader>
        <CardContent>
          {aiReviewLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              <p className="text-sm text-muted-foreground">AI is analyzing your {holdings.length} holdings...</p>
              <p className="text-xs text-muted-foreground">Takes 30-45 seconds</p>
            </div>
          ) : aiReview ? (
            <div className="space-y-4">
              {aiReview._markdown ? (
                <div>
                  <MarkdownMessage content={aiReview._markdown} />
                  <p className="text-[10px] text-muted-foreground italic mt-3">AI-generated analysis. Not investment advice.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <ScoreGauge score={aiReview.score || 0} />
                    <div className="md:col-span-3">
                      <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{aiReview.summary}</p>
                    </div>
                  </div>
                  {aiReview.allocation_analysis && (
                    <div className="p-3 rounded-lg bg-secondary/20 border border-border/30">
                      <p className="text-xs font-semibold mb-1">Allocation Analysis</p>
                      <p className="text-xs text-muted-foreground whitespace-pre-line">{aiReview.allocation_analysis}</p>
                    </div>
                  )}
                  {aiReview.risk_assessment && (
                    <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                      <p className="text-xs font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-500" /> Risk Assessment</p>
                      <p className="text-xs text-muted-foreground whitespace-pre-line">{aiReview.risk_assessment}</p>
                    </div>
                  )}
                  {aiReview.rebalancing?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold mb-2">Rebalancing Suggestions</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {aiReview.rebalancing.map((a, i) => <ActionCard key={i} action={a} />)}
                      </div>
                    </div>
                  )}
                  {aiReview.key_actions?.length > 0 && (
                    <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                      <p className="text-xs font-semibold mb-1.5">Key Actions</p>
                      {aiReview.key_actions.map((a, i) => <p key={i} className="text-xs text-muted-foreground mb-0.5">• {a}</p>)}
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground italic">AI-generated analysis. Not investment advice.</p>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-10">
              {aiReviewError && (
                <div className="mb-4 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 inline-block">
                  <p className="text-xs text-red-600 dark:text-red-400">Error: {aiReviewError}</p>
                </div>
              )}
              <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground mb-3">Get an AI-powered analysis of your portfolio</p>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" onClick={fetchAiReview}>
                <Sparkles className="w-3.5 h-3.5" /> {aiReviewError ? 'Retry' : 'Generate Review'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Portfolio Chat ──────────────────────────────────────── */}
      <Card className="border-border/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Ask AI About Your Portfolio</CardTitle>
        </CardHeader>
        <CardContent>
          {chatHistory.length === 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {QUICK_PROMPTS.map((q) => (
                <button key={q} onClick={() => handleAskAI(q)} className="px-3 py-1.5 text-xs rounded-full border border-border hover:bg-emerald-50 dark:hover:bg-emerald-950 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          )}
          <div className="max-h-[400px] overflow-y-auto space-y-3 mb-4">
            {chatHistory.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-xl ${msg.role === 'user' ? 'bg-emerald-600 text-white rounded-br-sm text-sm' : 'bg-secondary rounded-bl-sm'}`}>
                  {msg.role === 'user' ? msg.content : <MarkdownMessage content={msg.content} />}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-secondary px-4 py-3 rounded-xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Input placeholder="Ask about your portfolio..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAskAI()} />
            <Button onClick={() => handleAskAI()} disabled={!chatInput.trim() || chatLoading} className="bg-emerald-600 hover:bg-emerald-700">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Risk Metrics ───────────────────────────────────────── */}
      {risk && (
        <Card className="border-border/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
              <Shield className="w-4 h-4 text-emerald-500" /> Risk Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y md:divide-y-0 divide-border/50">
              <RiskGauge label="Beta" value={risk.beta} description="vs NIFTY 50" good={risk.beta != null && risk.beta <= 1} />
              <RiskGauge label="Sharpe Ratio" value={risk.sharpe} description="Risk-adj return" good={risk.sharpe != null && risk.sharpe > 1} />
              <RiskGauge label="Volatility" value={risk.volatility} unit="%" description="Annualized" good={risk.volatility != null && risk.volatility < 20} />
              <RiskGauge label="Max Drawdown" value={risk.maxDrawdown} unit="%" description="Peak-to-trough" good={risk.maxDrawdown != null && risk.maxDrawdown > -15} />
              <RiskGauge label="Diversification" value={risk.diversificationScore} unit="/100" description="Higher is better" good={risk.diversificationScore != null && risk.diversificationScore > 50} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Allocation Charts ──────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sectorAllocation.length > 0 && (
          <Card className="border-border/40">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Sector Allocation</CardTitle></CardHeader>
            <CardContent>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sectorAllocation} dataKey="weight" nameKey="sector" cx="50%" cy="50%" innerRadius={40} outerRadius={72} paddingAngle={2} label={({ sector, weight }) => `${sector} ${weight.toFixed(0)}%`}>
                      {sectorAllocation.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
        {holdingWeights.length > 0 && (
          <Card className="border-border/40">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Holding Concentration</CardTitle></CardHeader>
            <CardContent>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={holdingWeights} layout="vertical" margin={{ left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                    <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}%`} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={75} />
                    <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                    <Bar dataKey="weight" fill="#10b981" radius={[0, 4, 4, 0]} barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Correlation Matrix ──────────────────────────────────── */}
      {risk?.correlation && (
        <Card className="border-border/40">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Correlation Matrix (1Y)</CardTitle></CardHeader>
          <CardContent>
            <CorrelationMatrix data={risk.correlation} />
            <p className="text-[10px] text-muted-foreground mt-3">High correlation (red) reduces diversification. Low/negative correlation (green) improves risk-adjusted returns.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
